// src/pages/MyOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import ApiService from '../services/ApiService';
import '../styles/my-orders.css';
import SEOHead from '../components/SEO/SEOHead';

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Appel API pour récupérer les commandes de l'utilisateur
      const response = await ApiService.getUserOrders();

		// Gérer le nouveau format de réponse avec structure { commandes: [...] }
		if (response) {
		  if (Array.isArray(response)) {
			// Ancien format : tableau direct
			setOrders(response);
		  } else if (response.commandes && Array.isArray(response.commandes)) {
			// Nouveau format : objet avec propriété commandes
			setOrders(response.commandes);
		  } else {
			setOrders([]);
		  }
		} else {
		  setOrders([]);
		}
    } catch (err) {
      // console.error('Erreur lors de la récupération des commandes:', err);
      setError('Impossible de charger vos commandes. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year} à ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      return 'Date invalide';
    }
  };

  const formatPrice = (price) => {
    if (!price) return '0 FCFA';
    
    // Convertir en nombre et formater avec des espaces
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).replace(/\s/g, ' ') + ' FCFA';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'en_attente': 'En attente',
      'validee': 'Validée',
      'en_preparation': 'En préparation',
      'en_cours': 'En cours',
      'en_chemin': 'En chemin',
      'livree': 'Livrée',
      'annulee': 'Annulée'
    };
    
    return statusLabels[status] || status;
  };

  const getStatusClass = (status) => {
    if (status === 'livree') return 'status-delivered';
    if (status === 'annulee') return 'status-cancelled';
    if (status === 'en_chemin') return 'status-in-transit';
    if (status === 'validee' || status === 'en_preparation' || status === 'en_cours') return 'status-processing';
    return 'status-pending';
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handleBackClick = () => {
    navigate('/welcome-page');
  };

  return (
    <div className="my-orders-page">
    <SEOHead
      title="Mes commandes"
      noindex
    />
      <Header 
        title="Mes commandes" 
        showBackButton={true}
        onBackClick={handleBackClick}
      />

      <div className="my-orders-container">
        {loading && (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Chargement de vos commandes...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="error-text">{error}</p>
            <button className="retry-button" onClick={fetchOrders}>
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="empty-container">
            <svg 
              width="64" 
              height="64" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            <p className="empty-text">Vous n'avez pas encore de commande</p>
            <button 
              className="start-shopping-button"
              onClick={() => navigate('/welcome-page')}
            >
              Commencer mes achats
            </button>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="orders-list">
            {orders.map((order) => (
              <div 
                key={order.id_commande} 
                className="order-card"
                onClick={() => handleOrderClick(order.id_commande)}
              >
                <div className="order-header">
                  <div className="order-number">
                    <span className="order-label">Commande N°</span>
                    <span className="order-id">{order.id_commande}</span>
                  </div>
                  <span className={`order-status ${getStatusClass(order.statut)}`}>
                    {getStatusLabel(order.statut)}
                  </span>
                </div>

                <div className="order-details">
                  <div className="order-info">
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span className="order-date">{formatDate(order.date_commande)}</span>
                  </div>

                  <div className="order-amount">
                    <span className="amount-label">Montant :</span>
                    <span className="amount-value">{formatPrice(order.montant_total)}</span>
                  </div>
                </div>

                <div className="order-footer">
                  <span className="view-details">Voir les détails</span>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;